/**
 * Session State Manager
 * Handles serialization and deserialization of Import Wizard state for session recovery
 */

// State keys that should be persisted for session recovery
// NOTE: csvData and csvHeaders removed to reduce payload size by 50-90%
// These can be derived from filteredCsvData during session recovery
const PERSISTABLE_STATE_KEYS = [
  // 'csvData',            // REMOVED: Redundant - same as filteredCsvData before column mapping
  // 'csvHeaders',         // REMOVED: Can be derived from filteredCsvData keys
  'columnMappings',        // CSV header -> Odin field mappings (CRITICAL for data transformation)
  'filteredCsvData',       // Transformed data with mapped columns (this is the source of truth)
  'recordEdits',           // User edits per record index
  'recordStatuses',        // Status per record (matched/disregarded)
  'smartMappings',         // Auto-match rules (CSV value -> customer/branch)
  'manualOverrides',       // Set of record indices manually edited
  'selectedRecordIndex',   // Current record being reviewed
  'productMatches',        // Product variant key -> product ID mappings
  'productConfigurations', // New product configs by variant key
  'uniqueProducts',        // Extracted product variants
  'showCompletedRecords',  // UI toggle state
  'filteredBranches',      // Filtered branches per record index
  'productTypeMappings',   // CSV product type -> product_type mappings
  'productStrainMappings'  // CSV product strain -> product_strain mappings
]

/**
 * Prepares state for saving to database
 * Converts Sets to Arrays and filters to only persistable keys
 *
 * @param {Object} state - Current component state
 * @returns {Object} Serializable state object
 */
export const saveSessionState = (state) => {
  const stateToSave = {}

  PERSISTABLE_STATE_KEYS.forEach(key => {
    if (state[key] !== undefined) {
      // Convert Sets to Arrays for JSON serialization
      if (state[key] instanceof Set) {
        stateToSave[key] = Array.from(state[key])
      } else {
        stateToSave[key] = state[key]
      }
    }
  })

  // Add metadata for debugging/auditing
  stateToSave._savedAt = new Date().toISOString()
  stateToSave._version = '1.0.0' // For future migration compatibility

  return stateToSave
}

/**
 * Restores state from database
 * Converts Arrays back to Sets and validates structure
 *
 * @param {Object} sessionState - Saved state from database
 * @returns {Object} Restored state with proper data types
 */
export const loadSessionState = (sessionState) => {
  if (!sessionState || typeof sessionState !== 'object') {
    console.warn('Invalid session state:', sessionState)
    return {}
  }

  const restoredState = { ...sessionState }

  // Convert manualOverrides back to Set
  if (Array.isArray(restoredState.manualOverrides)) {
    restoredState.manualOverrides = new Set(restoredState.manualOverrides)
  } else {
    restoredState.manualOverrides = new Set()
  }

  // Ensure recordEdits is an object
  if (!restoredState.recordEdits || typeof restoredState.recordEdits !== 'object') {
    restoredState.recordEdits = {}
  }

  // Ensure recordStatuses is an object
  if (!restoredState.recordStatuses || typeof restoredState.recordStatuses !== 'object') {
    restoredState.recordStatuses = {}
  }

  // Ensure arrays are arrays
  if (!Array.isArray(restoredState.filteredCsvData)) {
    restoredState.filteredCsvData = []
  }
  if (!Array.isArray(restoredState.uniqueProducts)) {
    restoredState.uniqueProducts = []
  }

  // Derive csvData and csvHeaders from filteredCsvData if not present
  // This handles both old sessions (which have these fields) and new sessions (which don't)
  if (!restoredState.csvData || !Array.isArray(restoredState.csvData)) {
    // For backwards compatibility, use filteredCsvData as csvData
    restoredState.csvData = restoredState.filteredCsvData || []
  }
  if (!restoredState.csvHeaders || !Array.isArray(restoredState.csvHeaders)) {
    // Derive headers from first row of filteredCsvData
    if (restoredState.filteredCsvData?.[0]) {
      restoredState.csvHeaders = Object.keys(restoredState.filteredCsvData[0])
    } else {
      restoredState.csvHeaders = []
    }
  }

  // Ensure objects are objects
  if (typeof restoredState.columnMappings !== 'object') {
    restoredState.columnMappings = {}
  }
  if (typeof restoredState.smartMappings !== 'object') {
    restoredState.smartMappings = {}
  }
  if (typeof restoredState.productMatches !== 'object') {
    restoredState.productMatches = {}
  }
  if (typeof restoredState.productConfigurations !== 'object') {
    restoredState.productConfigurations = {}
  }
  if (typeof restoredState.filteredBranches !== 'object') {
    restoredState.filteredBranches = {}
  }
  if (typeof restoredState.productTypeMappings !== 'object') {
    restoredState.productTypeMappings = {}
  }
  if (typeof restoredState.productStrainMappings !== 'object') {
    restoredState.productStrainMappings = {}
  }

  // Ensure numbers are numbers
  if (typeof restoredState.selectedRecordIndex !== 'number') {
    restoredState.selectedRecordIndex = 0
  }

  // Ensure booleans are booleans
  if (typeof restoredState.showCompletedRecords !== 'boolean') {
    restoredState.showCompletedRecords = false
  }

  // Remove metadata
  delete restoredState._savedAt
  delete restoredState._version

  return restoredState
}

/**
 * Validates that a session state object has the minimum required data
 *
 * @param {Object} sessionState - State to validate
 * @returns {boolean} True if state has required data for recovery
 */
export const isValidSessionState = (sessionState) => {
  if (!sessionState || typeof sessionState !== 'object') {
    return false
  }

  // Must have filteredCsvData to be valid (this is now the source of truth)
  // csvData and csvHeaders are derived during load, so not checked here
  const hasFilteredData = Array.isArray(sessionState.filteredCsvData) && sessionState.filteredCsvData.length > 0

  return hasFilteredData
}

/**
 * Gets a human-readable summary of saved session state
 * Useful for debugging and UI display
 *
 * @param {Object} sessionState - Saved state
 * @returns {Object} Summary with counts and metadata
 */
export const getSessionStateSummary = (sessionState) => {
  if (!sessionState || typeof sessionState !== 'object') {
    return { valid: false }
  }

  const matchedCount = Object.values(sessionState.recordStatuses || {})
    .filter(status => status === 'matched').length
  const disregardedCount = Object.values(sessionState.recordStatuses || {})
    .filter(status => status === 'disregarded').length

  // Use filteredCsvData for record counts (csvData is no longer saved)
  const totalRecords = Array.isArray(sessionState.filteredCsvData) ? sessionState.filteredCsvData.length : 0

  return {
    valid: isValidSessionState(sessionState),
    totalRecords,
    matchedRecords: matchedCount,
    disregardedRecords: disregardedCount,
    unmatchedRecords: totalRecords - matchedCount - disregardedCount,
    currentRecord: sessionState.selectedRecordIndex || 0,
    productVariants: Array.isArray(sessionState.uniqueProducts) ? sessionState.uniqueProducts.length : 0,
    savedAt: sessionState._savedAt || null,
    version: sessionState._version || null
  }
}
